/**
 * Create Rotation Modal
 * Modal for creating new rotation with timing configuration
 */

import React, { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useQueryClient } from '@tanstack/react-query'
import { Dialog, Button, FormItem, Typogaphy, Select } from 'vnetwork-ui'
import { Plus, X, ChevronDown, ChevronRight } from 'lucide-react'

import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'

import { createRotation } from '@/modules/Oncall/services'
import { ONCALL_QUERY_KEYS } from '@/modules/Oncall/constants'
import {
    CreateRotationPayload,
    TimingType,
    ShiftFrequency,
    DayOfWeek,
    TimeSlotPayload,
} from '@/modules/Oncall/types/payload'

const DAYS_OF_WEEK: { value: DayOfWeek; label: string }[] = [
    { value: 0, label: 'sun' },
    { value: 1, label: 'mon' },
    { value: 2, label: 'tue' },
    { value: 3, label: 'wed' },
    { value: 4, label: 'thu' },
    { value: 5, label: 'fri' },
    { value: 6, label: 'sat' },
]

interface FormData {
    oncall_rotation_name: string
    shift_frequency: ShiftFrequency
    timing_type: TimingType
    rotation_start_date: string
    time_slots: TimeSlotPayload[]
}

export interface CreateRotationModalProps {
    open: boolean
    onClose: () => void
    scheduleId: number
    refetch?: () => void
}

const createRotationSchema = yup.object({
    oncall_rotation_name: yup.string().required('rotation_name_required'),
    shift_frequency: yup
        .mixed<ShiftFrequency>()
        .oneOf(['daily', 'weekly'])
        .required(),
    timing_type: yup
        .mixed<TimingType>()
        .oneOf(['everyday', 'custom_days'])
        .required(),
    rotation_start_date: yup.string().required('rotation_start_date_required'),
    time_slots: yup
        .array()
        .of(
            yup.object({
                day_from: yup.mixed<DayOfWeek>().required(),
                time_from: yup.string().required(),
                day_to: yup.mixed<DayOfWeek>().required(),
                time_to: yup.string().required(),
            })
        )
        .default([]),
})

const CreateRotationModal: React.FC<CreateRotationModalProps> = ({
    open,
    onClose,
    scheduleId,
    refetch,
}) => {
    const { t: translate } = useTranslation('oncall')
    const queryClient = useQueryClient()

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { isSubmitting },
    } = useForm<FormData>({
        mode: 'onSubmit',
        defaultValues: {
            oncall_rotation_name: '',
            shift_frequency: 'daily',
            timing_type: 'everyday',
            rotation_start_date: new Date().toISOString().split('T')[0],
            time_slots: [],
        },
        resolver: yupResolver(createRotationSchema),
    })

    const watchTimingType = useWatch({ control, name: 'timing_type' })
    const watchTimeSlots = useWatch({ control, name: 'time_slots' }) || []
    const [expandedSlot, setExpandedSlot] = useState<number | null>(null)

    const { mutateAsync: doCreate, isPending: isCreating } =
        useMutationWithHandler(
            {
                mutationFn: (payload: CreateRotationPayload) =>
                    createRotation({ scheduleId, data: payload }),
            },
            {
                successMessage: translate(
                    'rotation_created',
                    'Rotation created'
                ),
                reactQueryKeys: [ONCALL_QUERY_KEYS.ROTATIONS],
            }
        )

    const formatSlotSummary = (slot: TimeSlotPayload) => {
        const dayLabels = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7']
        return `${dayLabels[slot.day_from]} ${slot.time_from} → ${dayLabels[slot.day_to]} ${slot.time_to}`
    }

    const addTimeSlot = () => {
        const newSlot: TimeSlotPayload = {
            day_from: 1,
            time_from: '08:00',
            day_to: 5,
            time_to: '18:00',
        }
        const newSlots = [...watchTimeSlots, newSlot]
        setValue('time_slots', newSlots)
        setExpandedSlot(newSlots.length - 1)
    }

    const removeTimeSlot = (index: number) => {
        setValue(
            'time_slots',
            watchTimeSlots.filter((_slot, i) => i !== index)
        )
    }

    const updateTimeSlot = (
        index: number,
        field: keyof TimeSlotPayload,
        value: string | number
    ) => {
        const updated = [...watchTimeSlots]
        updated[index] = { ...updated[index], [field]: value }
        setValue('time_slots', updated)
    }

    const handleCreate = async (formData: FormData) => {
        try {
            const payload: CreateRotationPayload = {
                oncall_rotation_name: formData.oncall_rotation_name,
                shift_frequency: formData.shift_frequency,
                rotation_start_date: formData.rotation_start_date,
                is_active: true,
                timing: {
                    timing_type: formData.timing_type,
                    time_slots:
                        formData.timing_type === 'custom_days'
                            ? formData.time_slots
                            : [],
                },
            }
            await doCreate(payload)
            queryClient.invalidateQueries({
                queryKey: [ONCALL_QUERY_KEYS.ROTATIONS, scheduleId],
            })
            reset()
            refetch?.()
            onClose()
        } catch (error) {
            console.error(error)
        }
    }

    const handleClose = () => {
        reset()
        onClose()
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            title={translate('create_rotation', 'Create Rotation')}
            maxWidth="sm"
        >
            <form onSubmit={handleSubmit(handleCreate)}>
                <Typogaphy size="small" className="text-gray-600 mb-4">
                    {translate(
                        'create_rotation_description',
                        'Create a new rotation for this schedule with timing configuration.'
                    )}
                </Typogaphy>

                <div className="space-y-4">
                    {/* Rotation Name */}
                    <FormItem
                        control={control}
                        name="oncall_rotation_name"
                        type="textfield"
                        label={`${translate('rotation_name', 'Rotation Name')} *`}
                        placeholder={translate(
                            'rotation_name_placeholder',
                            'e.g., Weekday, Weekend, After-hours'
                        )}
                    />

                    {/* Shift Frequency */}
                    <FormItem
                        control={control}
                        name="shift_frequency"
                        type="select"
                        label={translate('shift_frequency', 'Shift Frequency')}
                        options={[
                            {
                                label: translate('daily', 'Daily'),
                                value: 'daily',
                            },
                            {
                                label: translate('weekly', 'Weekly'),
                                value: 'weekly',
                            },
                        ]}
                    />

                    {/* Rotation Start Date */}
                    <FormItem
                        control={control}
                        name="rotation_start_date"
                        type="date"
                        label={`${translate('rotation_start_date', 'Ngày bắt đầu xoay vòng')} *`}
                    />

                    {/* Timing Type */}
                    <FormItem
                        control={control}
                        name="timing_type"
                        type="select"
                        label={translate('timing_type', 'Timing')}
                        options={[
                            {
                                label: translate('everyday', 'Everyday (24/7)'),
                                value: 'everyday',
                            },
                            {
                                label: translate('custom_days', 'Custom Days'),
                                value: 'custom_days',
                            },
                        ]}
                    />

                    {/* Time Slots - only show for custom_days */}
                    {watchTimingType === 'custom_days' && (
                        <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">
                                    {translate('time_slots', 'Time Slots')}
                                </span>
                                <Button
                                    size="small"
                                    type="button"
                                    onClick={addTimeSlot}
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    {translate('add_slot', 'Add Slot')}
                                </Button>
                            </div>

                            {watchTimeSlots.length === 0 ? (
                                <Typogaphy
                                    size="small"
                                    className="text-gray-500 text-center py-4"
                                >
                                    {translate(
                                        'no_time_slots',
                                        'No time slots configured'
                                    )}
                                </Typogaphy>
                            ) : (
                                <div className="space-y-2">
                                    {watchTimeSlots.map((slot, index) => {
                                        const isExpanded =
                                            expandedSlot === index
                                        return (
                                            <div
                                                key={index}
                                                className="bg-white rounded border"
                                            >
                                                {/* Collapsed header — click to expand */}
                                                <div
                                                    className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-50"
                                                    onClick={() =>
                                                        setExpandedSlot(
                                                            isExpanded
                                                                ? null
                                                                : index
                                                        )
                                                    }
                                                >
                                                    <div className="flex items-center gap-2">
                                                        {isExpanded ? (
                                                            <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                                                        ) : (
                                                            <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                                                        )}
                                                        <span className="text-xs font-medium text-gray-600">
                                                            {formatSlotSummary(
                                                                slot
                                                            )}
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="text-gray-400 hover:text-red-500"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            removeTimeSlot(
                                                                index
                                                            )
                                                            if (
                                                                expandedSlot ===
                                                                index
                                                            )
                                                                setExpandedSlot(
                                                                    null
                                                                )
                                                        }}
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>

                                                {/* Expanded form */}
                                                {isExpanded && (
                                                    <div className="px-3 pb-3 border-t">
                                                        <div className="mt-3 mb-3">
                                                            <span className="text-xs text-gray-500 mb-1.5 block">
                                                                {translate(
                                                                    'from',
                                                                    'From'
                                                                )}
                                                                :
                                                            </span>
                                                            <div className="grid grid-cols-2 gap-3">
                                                                <Select
                                                                    size="small"
                                                                    value={
                                                                        slot.day_from
                                                                    }
                                                                    onChange={(
                                                                        value
                                                                    ) =>
                                                                        updateTimeSlot(
                                                                            index,
                                                                            'day_from',
                                                                            Number(
                                                                                value
                                                                            )
                                                                        )
                                                                    }
                                                                    options={DAYS_OF_WEEK.map(
                                                                        (
                                                                            d
                                                                        ) => ({
                                                                            label: translate(
                                                                                d.label
                                                                            ),
                                                                            value: d.value,
                                                                        })
                                                                    )}
                                                                />
                                                                <Select
                                                                    size="small"
                                                                    value={
                                                                        slot.time_from
                                                                    }
                                                                    onChange={(
                                                                        value
                                                                    ) =>
                                                                        updateTimeSlot(
                                                                            index,
                                                                            'time_from',
                                                                            String(
                                                                                value
                                                                            )
                                                                        )
                                                                    }
                                                                    options={Array.from(
                                                                        {
                                                                            length: 48,
                                                                        },
                                                                        (
                                                                            _,
                                                                            i
                                                                        ) => {
                                                                            const h =
                                                                                String(
                                                                                    Math.floor(
                                                                                        i /
                                                                                            2
                                                                                    )
                                                                                ).padStart(
                                                                                    2,
                                                                                    '0'
                                                                                )
                                                                            const m =
                                                                                i %
                                                                                    2 ===
                                                                                0
                                                                                    ? '00'
                                                                                    : '30'
                                                                            return {
                                                                                label: `${h}:${m}`,
                                                                                value: `${h}:${m}`,
                                                                            }
                                                                        }
                                                                    )}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <span className="text-xs text-gray-500 mb-1.5 block">
                                                                {translate(
                                                                    'to',
                                                                    'To'
                                                                )}
                                                                :
                                                            </span>
                                                            <div className="grid grid-cols-2 gap-3">
                                                                <Select
                                                                    size="small"
                                                                    value={
                                                                        slot.day_to
                                                                    }
                                                                    onChange={(
                                                                        value
                                                                    ) =>
                                                                        updateTimeSlot(
                                                                            index,
                                                                            'day_to',
                                                                            Number(
                                                                                value
                                                                            )
                                                                        )
                                                                    }
                                                                    options={DAYS_OF_WEEK.map(
                                                                        (
                                                                            d
                                                                        ) => ({
                                                                            label: translate(
                                                                                d.label
                                                                            ),
                                                                            value: d.value,
                                                                        })
                                                                    )}
                                                                />
                                                                <Select
                                                                    size="small"
                                                                    value={
                                                                        slot.time_to
                                                                    }
                                                                    onChange={(
                                                                        value
                                                                    ) =>
                                                                        updateTimeSlot(
                                                                            index,
                                                                            'time_to',
                                                                            String(
                                                                                value
                                                                            )
                                                                        )
                                                                    }
                                                                    options={Array.from(
                                                                        {
                                                                            length: 48,
                                                                        },
                                                                        (
                                                                            _,
                                                                            i
                                                                        ) => {
                                                                            const h =
                                                                                String(
                                                                                    Math.floor(
                                                                                        i /
                                                                                            2
                                                                                    )
                                                                                ).padStart(
                                                                                    2,
                                                                                    '0'
                                                                                )
                                                                            const m =
                                                                                i %
                                                                                    2 ===
                                                                                0
                                                                                    ? '00'
                                                                                    : '30'
                                                                            return {
                                                                                label: `${h}:${m}`,
                                                                                value: `${h}:${m}`,
                                                                            }
                                                                        }
                                                                    )}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="bg-blue-50 p-3 rounded-lg">
                        <Typogaphy size="small" className="text-blue-700">
                            {translate(
                                'create_rotation_note',
                                'You can add members to this rotation after creation.'
                            )}
                        </Typogaphy>
                    </div>
                </div>

                <div className="flex justify-end gap-4 mt-6">
                    <Button
                        variant="outlined"
                        type="button"
                        onClick={handleClose}
                    >
                        {translate('cancel', 'Cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        type="submit"
                        loading={isCreating}
                        disabled={isSubmitting || isCreating}
                    >
                        {translate('create', 'Create')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default CreateRotationModal
